import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import PopTipCDS from '@sharedCR/coreDesignSystem/PopTipCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PopperPlacementType } from 'constants/popover';

import {
  selectPendingChangesAuthorColorMap,
  selectPendingChangesAuthors,
} from 'store/loanApplications/selectors/pendingChangesAuthors.selectors';

import { AUTHOR_DOTS_POPPER_PROPS } from './PendingChangeAuthorDots.constants';
import {
  dotsWrapperStyles,
  getDotStyles,
  tooltipContentStyles,
  tooltipRowHeaderStyles,
  tooltipRowStyles,
  tooltipValuesStyles,
} from './PendingChangeAuthorDots.styles';
import { PendingChangeAuthorDotsTestIds } from './PendingChangeAuthorDots.testIds';
import { buildPendingChangeAuthorRows } from './PendingChangeAuthorDots.utils';

/**
 * Colored dot per author of a pending field change; hovering shows who changed it, when, and
 * old -> new. Renders nothing without authors.
 */
const PendingChangeAuthorDots = ({ authors = null, fieldLabel = '' }) => {
  const authorsList = useSelector(selectPendingChangesAuthors);
  const colorMap = useSelector(selectPendingChangesAuthorColorMap);

  const rows = useMemo(
    () =>
      buildPendingChangeAuthorRows({
        authors,
        authorsByKey: _.keyBy(authorsList, 'key'),
        colorMap,
      }),
    [authors, authorsList, colorMap],
  );

  if (_.isEmpty(rows)) {
    return null;
  }

  const tooltipContent = (
    <Box sx={tooltipContentStyles}>
      {!!fieldLabel && (
        <Typography variant="bold12">
          {rows.length > 1 ? `${fieldLabel} · changed by ${rows.length} people` : fieldLabel}
        </Typography>
      )}
      {rows.map(row => (
        <Box key={row.key} sx={tooltipRowStyles}>
          <Box sx={tooltipRowHeaderStyles}>
            <Box sx={getDotStyles(row.color)} />
            <Typography variant="medium12">{row.title}</Typography>
            <Typography color="text.secondary" variant="regular12">
              {row.time}
            </Typography>
          </Box>
          <Typography sx={tooltipValuesStyles} variant="regular12">
            {`${row.oldValue} → ${row.newValue}`}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <PopTipCDS
      arrow
      PopperProps={AUTHOR_DOTS_POPPER_PROPS}
      placement={PopperPlacementType.TOP}
      title={tooltipContent}
    >
      <Box
        component="span"
        data-testid={PendingChangeAuthorDotsTestIds.DOTS}
        sx={dotsWrapperStyles}
      >
        {rows.map(row => (
          <Box component="span" key={row.key} sx={getDotStyles(row.color)} />
        ))}
      </Box>
    </PopTipCDS>
  );
};

PendingChangeAuthorDots.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
      createdDate: PropTypes.string,
    }),
  ),
  fieldLabel: PropTypes.string,
};

export default memo(PendingChangeAuthorDots);
